import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle, ShieldCheck, Loader2 } from 'lucide-react';
import { PARTICIPATION_AGREEMENT, SAFEGUARDING_AGREEMENT } from '@/data/agreements';
import { AgreementModal } from '@/components/AgreementModal';

export default function AcceptAgreementsPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [acceptP, setAcceptP] = useState(false);
  const [acceptS, setAcceptS] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [viewAgreement, setViewAgreement] = useState(null);

  useEffect(() => {
    if (!token) { setLoading(false); setError('Invalid agreement acceptance link.'); return; }
    api.get(`/agreement-acceptance/${token}`)
      .then(r => setInfo(r.data))
      .catch(err => setError(err.response?.data?.detail || 'This link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    if (!acceptP || !acceptS) return;
    setSubmitting(true);
    setError('');
    try {
      await api.post('/agreement-acceptance/accept', { token, accept_participation: true, accept_safeguarding: true });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Acceptance failed.');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#F97D6A]" />
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4" data-testid="accept-agreements-error">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 max-w-md text-center">
          <h1 className="text-xl font-bold text-[#1F2937] font-['Outfit']">Agreement Acceptance</h1>
          <p className="text-sm text-red-600 mt-3">{error}</p>
          <p className="text-xs text-[#9CA3AF] mt-2">Please contact your school administrator for a new link.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4" data-testid="accept-agreements-success">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-7 w-7 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Agreements Accepted</h1>
          <p className="text-sm text-[#4B5563] mt-2">
            Both agreements have been successfully accepted for <strong>{info?.school_name}</strong>.
          </p>
          <p className="text-sm text-[#4B5563] mt-2">
            Accepted copies have been sent to your school's registered email. You will be notified when your school is activated.
          </p>
        </div>
      </div>
    );
  }

  const authRep = info?.authorized_representative || {};

  return (
    <div className="min-h-[calc(100vh-64px)] py-8 sm:py-12 px-4" data-testid="accept-agreements-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-[#FFF0ED] flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="h-6 w-6 text-[#F97D6A]" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit']">School Agreement Acceptance</h1>
          <p className="text-sm text-[#6B7280] mt-2">Review and accept both agreements for <strong>{info?.school_name}</strong></p>
        </div>

        {/* School & Signatory Info */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-7 mb-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-[#9CA3AF] text-xs font-medium">School</span>
              <p className="text-[#1F2937] font-semibold">{info?.school_name}</p>
            </div>
            <div>
              <span className="text-[#9CA3AF] text-xs font-medium">Authorized Representative</span>
              <p className="text-[#1F2937] font-semibold">{authRep.name} <span className="text-[#6B7280] font-normal">({authRep.designation})</span></p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">{error}</div>
        )}

        {/* Agreement 1 */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-7 mb-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-[#1F2937] font-['Outfit']">School Participation Agreement</h3>
              <p className="text-xs text-[#9CA3AF]">Version 1.0</p>
            </div>
            <Button type="button" variant="outline" size="sm" className="text-xs rounded-lg border-[#F97D6A] text-[#F97D6A] hover:bg-[#FFF0ED]" onClick={() => setViewAgreement('participation')} data-testid="read-participation-btn">
              Read Agreement
            </Button>
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <Checkbox checked={acceptP} onCheckedChange={setAcceptP} className="mt-0.5" data-testid="accept-participation" />
            <span className="text-sm text-[#4B5563]">{PARTICIPATION_AGREEMENT.acceptanceStatement}</span>
          </label>
        </div>

        {/* Agreement 2 */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-7 mb-6" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-[#1F2937] font-['Outfit']">School Safeguarding Responsibilities & Acknowledgment</h3>
              <p className="text-xs text-[#9CA3AF]">Version 1.0</p>
            </div>
            <Button type="button" variant="outline" size="sm" className="text-xs rounded-lg border-[#F97D6A] text-[#F97D6A] hover:bg-[#FFF0ED]" onClick={() => setViewAgreement('safeguarding')} data-testid="read-safeguarding-btn">
              Read Agreement
            </Button>
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <Checkbox checked={acceptS} onCheckedChange={setAcceptS} className="mt-0.5" data-testid="accept-safeguarding" />
            <span className="text-sm text-[#4B5563]">{SAFEGUARDING_AGREEMENT.acceptanceStatement}</span>
          </label>
        </div>

        {/* Submit */}
        <div className="text-center">
          <Button onClick={handleSubmit} disabled={submitting || !acceptP || !acceptS} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold px-8 py-3 h-auto text-base disabled:opacity-50" data-testid="confirm-accept-btn">
            {submitting ? 'Submitting...' : 'Confirm & Accept Agreements'}
          </Button>
        </div>

        {/* Agreement Modals */}
        <AgreementModal
          open={viewAgreement === 'participation'}
          onClose={() => setViewAgreement(null)}
          agreement={PARTICIPATION_AGREEMENT}
          acceptanceRecord={{
            school_name: info?.school_name,
            signatory_name: authRep.name,
            signatory_designation: authRep.designation,
            version: '1.0',
          }}
        />
        <AgreementModal
          open={viewAgreement === 'safeguarding'}
          onClose={() => setViewAgreement(null)}
          agreement={SAFEGUARDING_AGREEMENT}
          acceptanceRecord={{
            school_name: info?.school_name,
            signatory_name: authRep.name,
            signatory_designation: authRep.designation,
            version: '1.0',
          }}
        />
      </div>
    </div>
  );
}
